// @flow
import React, { Component } from 'react';
import PageTemplate from 'components/base/PageTemplate';
import MainMobileHeadContainer from 'containers/main/MainMobileHeadContainer';
import MainHeadContainer from 'containers/main/MainHeadContainer';
import { type ContextRouter } from 'react-router-dom';
import './AI.scss';

const MEILISEARCH_HOST = 'http://20.196.152.254:7700';
const MEILISEARCH_KEY = 'fHta7TBieefD8psv8H3lawnyVYXgY54G';

type Message = {
  role: 'user' | 'assistant',
  content: string,
  products?: Array<Object>,
};

type State = {
  messages: Array<Message>,
  inputValue: string,
  isProcessing: boolean,
  conversationHistory: Array<{ role: string, content: string }>,
};

type Props = ContextRouter;

const getContentFromParsed = (parsed: Object): string => {
  const { choices } = parsed;
  if (!choices || !choices[0] || !choices[0].delta) return '';
  const { content } = choices[0].delta;
  return content || '';
};

const parseSSELines = (text: string): string => {
  const lines = text.split('\n');
  let result = '';
  lines.forEach(line => {
    if (line.startsWith('data: ') && line !== 'data: [DONE]') {
      try {
        const parsed = JSON.parse(line.slice(6));
        result += getContentFromParsed(parsed);
      } catch (e) {
        // ignore parse errors
      }
    }
  });
  return result;
};

class AI extends Component<Props, State> {
  chatMessagesRef: { current: null | HTMLDivElement };
  inputRef: { current: null | HTMLTextAreaElement };

  constructor(props: Props) {
    super(props);
    this.state = {
      messages: [],
      inputValue: '',
      isProcessing: false,
      conversationHistory: [],
    };
    this.chatMessagesRef = React.createRef();
    this.inputRef = React.createRef();
  }

  scrollToBottom = () => {
    if (this.chatMessagesRef.current) {
      this.chatMessagesRef.current.scrollTop = this.chatMessagesRef.current.scrollHeight;
    }
  };

  handleInputChange = (e: SyntheticInputEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    this.setState({ inputValue: textarea.value });
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  handleKeyDown = (e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      this.sendMessage();
    }
  };

  sendExample = (text: string) => {
    this.setState({ inputValue: text }, () => {
      this.sendMessage();
    });
  };

  extractSearchParams = async (): Promise<{ keywords: string, maxPrice: ?number, minPrice: ?number }> => {
    const { conversationHistory } = this.state;
    try {
      const contextSummary = conversationHistory.map(m => `${m.role}: ${m.content}`).join('\n');

      const response = await fetch(`${MEILISEARCH_HOST}/chats/research-recommender/chat/completions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${MEILISEARCH_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `대화 내용을 분석하여 제품 검색 조건을 JSON으로 추출하세요.

반드시 아래 JSON 형식으로만 응답하세요 (다른 텍스트 없이):
{
  "keywords": "검색할 제품 키워드 (예: 단백질 정제 컬럼)",
  "maxPrice": 최대가격숫자또는null,
  "minPrice": 최소가격숫자또는null
}`,
            },
            {
              role: 'user',
              content: `대화 내용:\n${contextSummary}\n\n위 대화에서 검색할 제품과 가격 조건을 추출하세요.`,
            },
          ],
          stream: false,
        }),
      });

      const text = await response.text();
      const jsonStr = parseSSELines(text);

      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Search params extraction error:', error);
    }

    const lastUserMsg = conversationHistory.filter(m => m.role === 'user').pop();
    return { keywords: (lastUserMsg && lastUserMsg.content) || '', maxPrice: null, minPrice: null };
  };

  searchProducts = async (params: { keywords: string, maxPrice: ?number, minPrice: ?number }): Promise<Array<Object>> => {
    const { keywords, maxPrice, minPrice } = params;
    const filter = [];
    if (maxPrice) filter.push(`min_price <= ${maxPrice}`);
    if (minPrice) filter.push(`min_price >= ${minPrice}`);

    const searchBody: Object = {
      q: keywords,
      limit: 6,
      attributesToRetrieve: ['id', 'title', 'brand', 'min_price', 'max_price', 'url_slug', 'thumbnail', 'username'],
    };

    if (filter.length > 0) {
      searchBody.filter = filter.join(' AND ');
    }

    try {
      const response = await fetch(`${MEILISEARCH_HOST}/indexes/products/search`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${MEILISEARCH_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchBody),
      });

      const data = await response.json();
      return data.hits || [];
    } catch (error) {
      console.error('Product search error:', error);
      return [];
    }
  };

  processStreamChunk = (
    reader: Object,
    decoder: Object,
    fullResponse: string,
    message: string,
    conversationHistory: Array<{ role: string, content: string }>,
  ) => {
    reader.read().then(({ done, value }: { done: boolean, value: any }) => {
      if (done) {
        const newHistory = [
          ...conversationHistory,
          { role: 'user', content: message },
          { role: 'assistant', content: fullResponse },
        ];
        this.setState({ conversationHistory: newHistory });
        this.finishMessage(fullResponse);
        return;
      }

      const text = decoder.decode(value);
      const newContent = parseSSELines(text);
      const updatedResponse = fullResponse + newContent;

      if (newContent) {
        this.setState(prevState => {
          const newMessages = [...prevState.messages];
          newMessages[newMessages.length - 1] = {
            role: 'assistant',
            content: updatedResponse,
          };
          return { messages: newMessages };
        });
        this.scrollToBottom();
      }

      this.processStreamChunk(reader, decoder, updatedResponse, message, conversationHistory);
    });
  };

  finishMessage = async (fullResponse: string) => {
    const searchParams = await this.extractSearchParams();
    const products = await this.searchProducts(searchParams);

    if (products.length > 0) {
      this.setState(prevState => {
        const newMessages = [...prevState.messages];
        newMessages[newMessages.length - 1] = {
          ...newMessages[newMessages.length - 1],
          products,
        };
        return { messages: newMessages };
      });
      this.scrollToBottom();
    }

    this.setState({ isProcessing: false });
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  sendMessage = async () => {
    const { inputValue, isProcessing, conversationHistory, messages } = this.state;
    const message = inputValue.trim();
    if (!message || isProcessing) return;

    this.setState({
      isProcessing: true,
      inputValue: '',
      messages: [...messages, { role: 'user', content: message }],
    });

    if (this.inputRef.current) {
      this.inputRef.current.style.height = 'auto';
    }

    setTimeout(this.scrollToBottom, 0);

    try {
      const response = await fetch(`${MEILISEARCH_HOST}/chats/research-recommender/chat/completions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${MEILISEARCH_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `당신은 캐시바이의 연구제품 전문 AI 어시스턴트입니다.

역할:
- 사용자의 연구실험 요청을 분석합니다
- 어떤 종류의 제품이 필요한지 설명합니다
- 제품 선택 시 고려해야 할 팁을 제공합니다

중요 규칙:
- 구체적인 제품명이나 브랜드를 직접 나열하지 마세요
- 실제 제품은 아래에 자동으로 표시됩니다

응답 형식 (짧고 간결하게):
1. 요청 이해 (1-2문장)
2. 필요한 제품 카테고리 설명
3. 선택 팁 또는 주의사항 (1-2문장)

3-5문장 이내로 짧게 응답하세요.`,
            },
            ...conversationHistory,
            { role: 'user', content: message },
          ],
          stream: true,
        }),
      });

      const reader = response.body && response.body.getReader();
      const decoder = new TextDecoder();

      this.setState(prevState => ({
        messages: [...prevState.messages, { role: 'assistant', content: '' }],
      }));

      if (reader) {
        this.processStreamChunk(reader, decoder, '', message, conversationHistory);
      } else {
        this.finishMessage('');
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState(prevState => ({
        messages: [
          ...prevState.messages,
          { role: 'assistant', content: '죄송합니다. 오류가 발생했습니다. 다시 시도해주세요.' },
        ],
        isProcessing: false,
      }));
    }
  };

  renderProducts = (products: Array<Object>) => (
    <div className="ai-products-section">
      <div className="ai-products-header">
        <h4>추천 제품</h4>
        <span className="ai-products-count">{products.length}개</span>
      </div>
      <div className="ai-products-grid">
        {products.map((p, idx) => {
          const productUrl = `/@${p.username || 'cacheby'}/products/${p.url_slug}`;
          return (
            <div className="ai-product-card" key={p.id || idx}>
              <div className="ai-product-image">
                {p.thumbnail ? (
                  <img
                    src={p.thumbnail}
                    alt={p.title}
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <h5>{p.title}</h5>
              <div className="ai-product-brand">{p.brand || '캐시바이'}</div>
              <div className="ai-product-price-row">
                {Number(p.min_price) > 0 ? (
                  <span className="ai-product-price">{Number(p.min_price).toLocaleString()}원</span>
                ) : (
                  <span className="ai-product-price-inquiry">가격 문의</span>
                )}
              </div>
              <a href={productUrl} className="ai-product-link">
                상세보기
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );

  renderMarkdown = (content: string) => {
    let html = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br />');

    html = html.replace(/^- (.*?)(<br \/>|$)/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');

    return { __html: html };
  };

  render() {
    const { messages, inputValue, isProcessing } = this.state;
    const hasMessages = messages.length > 0;

    const suggestions = [
      '세포배양 실험 장비 추천',
      'PCR 시약 목록',
      '단백질 정제 컬럼',
      '실험실 기본 세팅',
    ];

    return (
      <PageTemplate>
        <header>
          <MainHeadContainer />
          <MainMobileHeadContainer />
        </header>
        <div className={`ai-page ${hasMessages ? '' : 'ai-initial-state'}`}>
          <div className="ai-container">
            <div className="ai-chat-container">
              {hasMessages ? (
                <React.Fragment>
                  <div className="ai-chat-messages" ref={this.chatMessagesRef}>
                    <div className="ai-messages-inner">
                      {messages.map((msg, idx) => (
                        <div key={idx} className={`ai-message ai-message-${msg.role}`}>
                          <div
                            className="ai-message-content"
                            dangerouslySetInnerHTML={msg.role === 'assistant' ? this.renderMarkdown(msg.content) : undefined}
                          >
                            {msg.role === 'user' ? msg.content : null}
                          </div>
                          {msg.products && msg.products.length > 0 && this.renderProducts(msg.products)}
                        </div>
                      ))}
                      {isProcessing && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                        <div className="ai-message ai-message-assistant">
                          <div className="ai-typing">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ai-input-area">
                    <div className="ai-input-wrapper">
                      <div className="ai-input-box">
                        <textarea
                          ref={this.inputRef}
                          className="ai-input"
                          placeholder="무엇이든 물어보세요"
                          value={inputValue}
                          onChange={this.handleInputChange}
                          onKeyDown={this.handleKeyDown}
                          rows={1}
                        />
                        <button
                          className="ai-send-btn"
                          onClick={this.sendMessage}
                          disabled={isProcessing || !inputValue.trim()}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ) : (
                <div className="ai-welcome">
                  <h1 className="ai-welcome-title">무엇을 도와드릴까요?</h1>
                  <div className="ai-input-area">
                    <div className="ai-input-wrapper">
                      <div className="ai-input-box">
                        <textarea
                          ref={this.inputRef}
                          className="ai-input"
                          placeholder="무엇이든 물어보세요"
                          value={inputValue}
                          onChange={this.handleInputChange}
                          onKeyDown={this.handleKeyDown}
                          rows={1}
                        />
                        <button
                          className="ai-send-btn"
                          onClick={this.sendMessage}
                          disabled={isProcessing || !inputValue.trim()}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="ai-welcome-suggestions">
                    {suggestions.map((text, idx) => (
                      <button key={idx} className="ai-suggestion-btn" onClick={() => this.sendExample(text)}>
                        {text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageTemplate>
    );
  }
}

export default AI;



// WEBPACK FOOTER //
// ./src/pages/AI.js